// 音乐离线缓存工具
// 使用 IndexedDB 缓存元数据，Service Worker 缓存文件

import { getCoverStreamUrl, getMusicStreamUrl, getLyricsStreamUrl } from './musicStreamUrls';

const DB_NAME = 'TenryuMusicCache';
const DB_VERSION = 1;
const STORE_MUSIC = 'musics';
const STORE_LYRICS = 'lyrics';
const STORE_COVERS = 'covers';
const STORE_METADATA = 'metadata';

let db = null;

// 是否启用“离线音乐（Service Worker）”
// - 默认开启（localStorage 未设置时返回 true）
// - 可通过环境变量 REACT_APP_ENABLE_OFFLINE_MUSIC=0 来默认关闭
// - 用户可在 UI 中手动关闭/开启
// - 关闭后：禁止注册/使用 Service Worker，并尽量注销现有 SW
export const OFFLINE_MUSIC_ENABLED_KEY = 'tenryu:offline-music-enabled';

export function isOfflineMusicEnabled() {
  try {
    const v = localStorage.getItem(OFFLINE_MUSIC_ENABLED_KEY);
    if (v === null) {
      // 默认开启：如果用户没有显式设置，则启用离线音乐功能
      // 可以通过 .env 设置 REACT_APP_ENABLE_OFFLINE_MUSIC=0 来默认关闭
      const env = process.env.REACT_APP_ENABLE_OFFLINE_MUSIC;
      if (env === '0' || String(env).toLowerCase() === 'false') return false;
      return true; // 默认开启
    }
    return v === '1';
  } catch (_) {
    // 如果 localStorage 访问失败，检查环境变量
    const env = process.env.REACT_APP_ENABLE_OFFLINE_MUSIC;
    if (env === '0' || String(env).toLowerCase() === 'false') return false;
    return true; // 默认开启
  }
}

export async function setOfflineMusicEnabled(enabled) {
  try {
    localStorage.setItem(OFFLINE_MUSIC_ENABLED_KEY, enabled ? '1' : '0');
  } catch (_) {}
  // 通知页面：便于 UI 同步
  try {
    window.dispatchEvent(new CustomEvent('tenryu:offline-music-enabled-changed', { detail: { enabled: !!enabled } }));
  } catch (_) {}

  // 关闭时：立刻注销（注意：当前页面的 controller 可能需要刷新后才会完全解除）
  if (!enabled) {
    try {
      await unregisterServiceWorker();
    } catch (_) {}
  }
  return !!enabled;
}

// 初始化 IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // 创建音乐元数据存储
      if (!database.objectStoreNames.contains(STORE_MUSIC)) {
        const musicStore = database.createObjectStore(STORE_MUSIC, { keyPath: 'id' });
        musicStore.createIndex('file_path', 'file_path', { unique: false });
        musicStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // 创建歌词存储
      if (!database.objectStoreNames.contains(STORE_LYRICS)) {
        const lyricsStore = database.createObjectStore(STORE_LYRICS, { keyPath: 'file_path' });
        lyricsStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // 创建封面存储
      if (!database.objectStoreNames.contains(STORE_COVERS)) {
        const coverStore = database.createObjectStore(STORE_COVERS, { keyPath: 'file_path' });
        coverStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // 创建其他元数据存储
      if (!database.objectStoreNames.contains(STORE_METADATA)) {
        database.createObjectStore(STORE_METADATA, { keyPath: 'key' });
      }
    };
  });
};

// 注销 Service Worker（用于 admin 用户禁用离线功能）
export const unregisterServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      // 先取消激活，然后注销
      const unregisterResult = await registration.unregister();
      // 只在非 admin 场景打印日志（admin 场景由 AdminContext 统一管理日志）
      
      // 清空所有缓存
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames
            .filter(name => name.startsWith('tenryu-'))
            .map(name => caches.delete(name))
        );
        console.log('[Cache] All PWA caches cleared');
      }
      
      return unregisterResult;
    }
    return false;
  } catch (error) {
    console.error('[Cache] Service Worker unregistration failed:', error);
    return false;
  }
};

// 防重复注册标志
let isRegistering = false;
let lastRegistrationAttempt = null;
const REGISTRATION_DEBOUNCE_MS = 1000; // 1秒内的重复调用会被忽略

// 注册 Service Worker
export const registerServiceWorker = async (isAdmin = false) => {
  // 防重复调用：如果正在注册或最近刚尝试过，直接返回
  const now = Date.now();
  if (isRegistering || (lastRegistrationAttempt && (now - lastRegistrationAttempt) < REGISTRATION_DEBOUNCE_MS)) {
    // 静默返回，避免重复日志
    return null;
  }
  
  isRegistering = true;
  lastRegistrationAttempt = now;
  
  try {
    // 如果登录的是 admin 用户，不开启 PWA 所有离线功能
    if (isAdmin) {
      console.log('[Cache] Admin user detected, skipping Service Worker registration');
      // 如果已经注册了，先注销
      await unregisterServiceWorker();
      return null;
    }

  // 用户主动关闭“离线音乐”时，禁止注册/使用 Service Worker
  if (!isOfflineMusicEnabled()) {
    console.log('[Cache] Offline music disabled, skipping Service Worker registration');
    // 若已存在注册，尽量注销
    await unregisterServiceWorker();
    return null;
  }
  
  // 默认只在生产环境启用，避免本地开发被缓存“污染”。
  // 如需在开发环境测试离线：
  // - 控制台执行 `window.__ENABLE_SW_DEV__ = true` 后刷新；或
  // - localStorage 设置 `tenryu:enableSWDev=1` 后刷新（离线页会提供按钮）；或
  // - URL 参数 `?enableSwDev=1`（一次性）
  // 注意：在 CRA/Webpack 构建产物里，`process.env.NODE_ENV` 会被编译期替换成字符串常量。
  // 如果这里写成 `typeof process !== 'undefined' && process.env && ...`，构建后会误判为 false，导致永远不注册 SW。
  const isProd = process.env.NODE_ENV === 'production';
  const forceDev = (() => {
    try {
      if (typeof window === 'undefined') return false;
      if (window.__ENABLE_SW_DEV__) return true;
      try {
        if (window.location && window.location.search) {
          const sp = new URLSearchParams(window.location.search);
          if (sp.get('enableSwDev') === '1') return true;
        }
      } catch (_) {}
      try {
        return window.localStorage && window.localStorage.getItem('tenryu:enableSWDev') === '1';
      } catch (_) {}
      return false;
    } catch (_) {
      return false;
    }
    })();
    if (!isProd && !forceDev) return null;

    if (!('serviceWorker' in navigator)) return null;

    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

    // 监听更新：当新 SW 安装完成且当前页面已有 controller，说明“更新已就绪”。
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          try {
            window.dispatchEvent(new CustomEvent('tenryu:sw-update-available'));
          } catch (_) {}
          console.info('[Cache] New version available. Refresh to update.');
        }
      });
    });

    console.log('[Cache] Service Worker registered:', registration);

    // 等待 Service Worker 激活后，同步离线模式状态和 admin 状态
    const syncServiceWorkerState = (sw) => {
      if (!sw) return;
      try {
        // 同步离线模式状态
        const offlineModeEnabled = (() => {
          try {
            const value = localStorage.getItem('tenryu:offline-mode-enabled');
            return value === null ? true : value === '1';
          } catch (_) {
            return true;
          }
        })();
        
        sw.postMessage({
          type: 'SET_OFFLINE_MODE',
          enabled: offlineModeEnabled
        });
        
        // 同步 admin 状态（检查是否有 admin session hint）
        const isAdmin = (() => {
          try {
            return localStorage.getItem('tenryu_admin_session_hint') === '1';
          } catch (_) {
            return false;
          }
        })();
        
        sw.postMessage({
          type: 'SET_ADMIN_MODE',
          enabled: isAdmin
        });
      } catch (e) {
        console.warn('[Cache] Failed to sync Service Worker state:', e);
      }
    };
    
    if (navigator.serviceWorker.controller) {
      syncServiceWorkerState(navigator.serviceWorker.controller);
    } else {
      // 如果还没有 controller，等待 ready 后再发送
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
          syncServiceWorkerState(registration.active);
        }
      });
    }

    return registration;
  } catch (error) {
    console.error('[Cache] Service Worker registration failed:', error);
    return null;
  } finally {
    isRegistering = false;
  }
};

// 缓存音乐文件（通过 Service Worker）
export const cacheMusicFile = async (url) => {
  if (!isOfflineMusicEnabled()) return false;
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_MUSIC',
        url
      });
      return true;
    } catch (error) {
      console.error('[Cache] Failed to cache music file:', error);
      return false;
    }
  }
  return false;
};

// 保存音乐元数据到 IndexedDB
export const cacheMusicMetadata = async (music) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_MUSIC], 'readwrite');
    const store = transaction.objectStore(STORE_MUSIC);

    const musicData = {
      ...music,
      cached_at: new Date().toISOString()
    };

    await store.put(musicData);
    
    // 全站统一：音频缓存固定使用 .webm
    const audioUrlToCache = getMusicStreamUrl(music, 'webm');
    if (audioUrlToCache) {
      await cacheMusicFile(audioUrlToCache);
    }
    
    // 缓存封面（webp-only 直链）
    const coverUrlToCache = getCoverStreamUrl(music);
    if (coverUrlToCache) {
      await cacheMusicFile(coverUrlToCache);
    }

    return true;
  } catch (error) {
    console.error('[Cache] Failed to cache music metadata:', error);
    return false;
  }
};

// 从 IndexedDB 获取音乐元数据
export const getCachedMusicMetadata = async (musicId) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_MUSIC], 'readonly');
    const store = transaction.objectStore(STORE_MUSIC);

    return new Promise((resolve, reject) => {
      const request = store.get(musicId);
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[Cache] Failed to get cached music metadata:', error);
    return null;
  }
};

// 缓存歌词
export const cacheLyrics = async (filePath, lyrics, lyricsType) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_LYRICS], 'readwrite');
    const store = transaction.objectStore(STORE_LYRICS);

    const lyricsData = {
      file_path: filePath,
      lyrics,
      lyrics_type: lyricsType,
      cached_at: new Date().toISOString()
    };

    await store.put(lyricsData);
    
    // 如果歌词是文件路径，也缓存文件
    if (filePath && !filePath.startsWith('data:')) {
      await cacheMusicFile(filePath);
    }

    return true;
  } catch (error) {
    console.error('[Cache] Failed to cache lyrics:', error);
    return false;
  }
};

// 获取缓存的歌词
export const getCachedLyrics = async (filePath) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_LYRICS], 'readonly');
    const store = transaction.objectStore(STORE_LYRICS);

    return new Promise((resolve, reject) => {
      const request = store.get(filePath);
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[Cache] Failed to get cached lyrics:', error);
    return null;
  }
};

// 获取所有缓存的音乐列表
export const getAllCachedMusics = async () => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_MUSIC], 'readonly');
    const store = transaction.objectStore(STORE_MUSIC);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        resolve(request.result || []);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[Cache] Failed to get all cached musics:', error);
    return [];
  }
};

// 删除缓存的音乐
export const deleteCachedMusic = async (musicId) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_MUSIC], 'readwrite');
    const store = transaction.objectStore(STORE_MUSIC);

    // 获取音乐信息以删除相关文件
    const music = await getCachedMusicMetadata(musicId);
    
    await store.delete(musicId);
    
    // 如果音乐有歌词，也删除歌词缓存
    if (music && music.lyrics_type) {
      const lyricsTransaction = database.transaction([STORE_LYRICS], 'readwrite');
      const lyricsStore = lyricsTransaction.objectStore(STORE_LYRICS);
      const lyricsKey = getLyricsStreamUrl(music);
      if (lyricsKey) {
        await lyricsStore.delete(lyricsKey);
      }
    }

    return true;
  } catch (error) {
    console.error('[Cache] Failed to delete cached music:', error);
    return false;
  }
};

// 清空所有缓存
export const clearAllCache = async () => {
  try {
    // 清空 IndexedDB
    const database = await initDB();
    const stores = [STORE_MUSIC, STORE_LYRICS, STORE_COVERS, STORE_METADATA];
    
    for (const storeName of stores) {
      const transaction = database.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      await store.clear();
    }

    // 清空 Service Worker 缓存
    if (!isOfflineMusicEnabled()) return true;
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          if (event.data.success) {
            resolve(true);
          }
        };
        navigator.serviceWorker.controller.postMessage(
          { type: 'CLEAR_CACHE' },
          [messageChannel.port2]
        );
      });
    }

    return true;
  } catch (error) {
    console.error('[Cache] Failed to clear cache:', error);
    return false;
  }
};

// 获取缓存大小（文件数量）
export const getCacheSize = async () => {
  try {
    if (!isOfflineMusicEnabled()) return 0;
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data.size || 0);
        };
        navigator.serviceWorker.controller.postMessage(
          { type: 'GET_CACHE_SIZE' },
          [messageChannel.port2]
        );
        
        // 超时处理
        setTimeout(() => resolve(0), 1000);
      });
    }
    return 0;
  } catch (error) {
    console.error('[Cache] Failed to get cache size:', error);
    return 0;
  }
};

// 检查文件是否已缓存
export const isFileCached = async (url) => {
  try {
    if ('caches' in window) {
      const cache = await caches.open('tenryu-music-files-v1');
      const cached = await cache.match(url);
      return !!cached;
    }
    return false;
  } catch (error) {
    console.error('[Cache] Failed to check cache:', error);
    return false;
  }
};
